import { useCallback } from "react";
import { SxProps } from "@mui/material";
import { BasicOption } from "../../../components/universal/BaseAsyncAutocomplete";
import InfiniteScrollAutocomplete, {
  GetQueryVariablesFromPaginationAndInputArgs,
} from "../../../components/universal/InfiniteScroll/InfiniteScrollAutocomplete";
import {
  BulkAssignGoalTemplateJobCreatedByFragment,
  BulkAssignGoalTemplateJobCreatedByCursorConnectionQueryVariables,
  useBulkAssignGoalTemplateJobCreatedByCursorConnectionQuery,
} from "../../../types";

interface CreatedByMultiselectProps {
  selectedUsers: BulkAssignGoalTemplateJobCreatedByFragment[];
  searchText: string;
  onChangeSelectedUsers: (users: BulkAssignGoalTemplateJobCreatedByFragment[]) => void;
  sx?: SxProps;
}

export const CreatedByMultiselect = ({ onChangeSelectedUsers, selectedUsers, searchText, sx }: CreatedByMultiselectProps) => {
  const getQueryVariables = useCallback(
    ({ cursor, limit, input }: GetQueryVariablesFromPaginationAndInputArgs) => ({
      variables: {
        query: input,
        pagination: { cursor, limit },
        searchText,
      },
    }),
    [searchText]
  );

  const transformAndFilterOptions = useCallback(
    (edges: BulkAssignGoalTemplateJobCreatedByFragment[] | undefined): BulkAssignGoalTemplateJobCreatedByFragment[] => {
      const selectedUserIds = selectedUsers.map(u => u.id);
      return edges?.filter(u => !selectedUserIds.includes(u.id)) || [];
    },
    [selectedUsers]
  );

  return (
    <InfiniteScrollAutocomplete<
      "bulkAssignGoalTemplateJobCreatedByCursorConnection",
      BulkAssignGoalTemplateJobCreatedByFragment,
      BulkAssignGoalTemplateJobCreatedByCursorConnectionQueryVariables,
      BulkAssignGoalTemplateJobCreatedByFragment,
      true,
      undefined
    >
      sx={sx}
      multiple
      getOptionSelected={usersAreEqual}
      disableCloseOnSelect
      blurOnSelect={false}
      showStartAdornment={false}
      disableRootPadding
      edgesAreEqual={usersAreEqual}
      queryKey="bulkAssignGoalTemplateJobCreatedByCursorConnection"
      useCursorConnectionQuery={useBulkAssignGoalTemplateJobCreatedByCursorConnectionQuery}
      getQueryVariablesFromPaginationAndInput={getQueryVariables}
      transformAndFilterOptions={transformAndFilterOptions}
      renderOption={user => <BasicOption element={getUserLabel(user)} />}
      handleChange={(_, users) => onChangeSelectedUsers(users)}
      getOptionLabel={getUserLabel}
      getCustomOptionKey={(u: BulkAssignGoalTemplateJobCreatedByFragment) => u.id}
      noOptionsText="No Users Found"
      inputPlaceholder="Assigned By"
      value={selectedUsers}
      size="medium"
    />
  );
};

const getUserLabel = (user: BulkAssignGoalTemplateJobCreatedByFragment): string => {
  return `${user.firstName} ${user.lastName}`;
};

const usersAreEqual = (user1: BulkAssignGoalTemplateJobCreatedByFragment, user2: BulkAssignGoalTemplateJobCreatedByFragment) => {
  return user1.id === user2.id;
};
